/**
 * Roles Tab - click a role to open the permission editor modal.
 *
 * Clicking a role row opens a full-screen modal with a sidebar of
 * permission categories and a detail panel for toggling individual
 * permissions. Changes are saved immediately per toggle (PUT full set).
 *
 * Admin roles (is_admin=True) show all permissions as granted (read-only).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ChevronRight, Loader2, Pencil, Plus } from "lucide-react";
import { StatCards } from "@/admin/components/shared/stat-cards";

import { PageInfo } from "@/admin/components/page-info";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import api from "@/admin/lib/api";
import { useOrg } from "@/admin/hooks/use-org";
import { cn } from "@/shared/lib/utils";
import { useForm } from "@/shared/lib/forms";
import type { FormField } from "@/shared/lib/forms";
import { DataTable, type DataTableColumn } from "@/admin/components/data-table";
import {
  groupsByEntity,
  type Permission,
  type PermissionGroup,
} from "@/admin/lib/permissions";
import { PermissionEditorModal } from "@/admin/pages/settings/permission-editor-modal";
import { RoleTemplateSuggestions } from "@/admin/pages/settings/role-template-suggestions";
import {
  RoleEditDialog,
  type RoleEditValues,
} from "@/admin/pages/settings/role-edit-dialog";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import { toastApiError } from "@/shared/lib/api-error";
interface Role {
  uuid: string;
  name: string;
  description: string;
  is_admin: boolean;
  sso_role_identifier: string;
  permission_codenames: string[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RolesTab() {
  const { orgUuid } = useOrg();
  const { t } = useTranslation();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>(
    [],
  );
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [savingRoleUuid, setSavingRoleUuid] = useState<string | null>(null);
  // Rename / edit dialog (name + description only).
  const [editRole, setEditRole] = useState<Role | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Create dialog - name + description + an optional starting template.
  // Fine-grained permissions are configured after the role exists.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(
    null,
  );
  // Mirror the selection into a ref so the (memoised) submit handler reads the
  // latest value without re-initialising the form and clearing typed fields.
  const selectedTemplateIdRef = useRef<number | null>(null);
  const selectTemplate = useCallback((id: number | null) => {
    selectedTemplateIdRef.current = id;
    setSelectedTemplateId(id);
  }, []);

  const fetchData = useCallback(async () => {
    if (!orgUuid) {
      setLoading(false);
      return;
    }
    try {
      const [rolesRes, groupsRes, permsRes] = await Promise.all([
        api.get(`/organisations/${orgUuid}/roles/`),
        api.get("/auth/rbac/permission-groups/"),
        api.get("/auth/rbac/permissions/"),
      ]);
      setRoles(rolesRes.data.results ?? rolesRes.data);
      setPermissionGroups(groupsRes.data.results ?? groupsRes.data);
      setAllPermissions(permsRes.data.results ?? permsRes.data);
    } catch (err) {
      toastApiError(err, t("roles.errorLoadRoles"));
    } finally {
      setLoading(false);
    }
  }, [orgUuid, t]);

  useEffect(() => {
    fetchData();  
  }, [fetchData]);

  const selectedRoleData = selectedRole
    ? roles.find((r) => r.uuid === selectedRole.uuid) ?? null
    : null;
  const selectedGrantedSet = useMemo(() => {
    if (!selectedRoleData) return new Set<string>();
    if (selectedRoleData.is_admin)
      return new Set(allPermissions.map((p) => p.codename));
    return new Set(selectedRoleData.permission_codenames ?? []);
  }, [selectedRoleData, allPermissions]);

  /** Toggle a single permission codename on a role, PUT full set. */
  const toggleCodenameOnRole = useCallback(
    async (role: Role, codename: string | string[]) => {
      if (!orgUuid || role.is_admin) return;
      const current = new Set(role.permission_codenames ?? []);
      const codes = Array.isArray(codename) ? codename : [codename];
      for (const c of codes) {
        if (current.has(c)) current.delete(c);
        else current.add(c);
      }

      setSavingRoleUuid(role.uuid);
      try {
        await api.put(`/organisations/${orgUuid}/roles/${role.uuid}/`, {
          name: role.name,
          description: role.description,
          permission_codenames: Array.from(current),
        });
        setRoles((prev) =>
          prev.map((r) =>
            r.uuid === role.uuid
              ? { ...r, permission_codenames: Array.from(current) }
              : r,
          ),
        );
      } catch (err) {
        toastApiError(err, t("roles.errorUpdateRole"));
        fetchData();
      } finally {
        setSavingRoleUuid(null);
      }
    },
    [fetchData, orgUuid, t],
  );

  const handleEditSave = useCallback(
    async (values: RoleEditValues) => {
      if (!orgUuid || !editRole) return;
      setSavingEdit(true);
      try {
        // Partial PUT - name + description only; permissions untouched.
        await api.put(`/organisations/${orgUuid}/roles/${editRole.uuid}/`, {
          name: values.name,
          description: values.description,
        });
        setRoles((prev) =>
          prev.map((r) =>
            r.uuid === editRole.uuid
              ? { ...r, name: values.name, description: values.description }
              : r,
          ),
        );
        toast.success(t("roles.roleUpdated"));
        setEditRole(null);
      } catch (err) {
        toastApiError(err, t("roles.errorUpdateRole"));
      } finally {
        setSavingEdit(false);
      }
    },
    [editRole, orgUuid, t],
  );

  const createRoleFields = useMemo<FormField[]>(
    () => [
      {
        type: "text",
        name: "name",
        label: "Role Name",
        placeholder: "e.g. Dealer Manager",
        value: "",
        autoFocus: true,
        required: "Role name is required",
      },
      {
        type: "text",
        name: "description",
        label: "Description",
        placeholder: "What this role can do",
        value: "",
      },
      {
        type: "text",
        name: "sso_role_identifier",
        label: "SSO Role Identifier",
        placeholder: "e.g. admin, claims-reviewer (from IdP)",
        value: "",
        hint: "Maps an IdP role to this platform role on SSO login. Optional.",
      },
    ],
    [],
  );
  const { Form: CreateRoleForm, ref: createRoleFormRef } = useForm(
    {
      name: "roles-create-form",
      submit: "footer",
      fields: createRoleFields,
    },
    async (rawValues) => {
      if (!orgUuid) return;
      const values = rawValues as Record<string, string>;
      setSaving(true);
      try {
        const templateId = selectedTemplateIdRef.current;
        await api.post(`/organisations/${orgUuid}/roles/`, {
          name: String(values.name ?? "").trim(),
          description: String(values.description ?? "").trim(),
          sso_role_identifier: String(values.sso_role_identifier ?? "").trim(),
          ...(templateId != null ? { apply_group_ids: [templateId] } : {}),
        });
        toast.success(t("roles.roleCreated"));
        setDialogOpen(false);
        await fetchData();
      } catch (err) {
        toastApiError(err, t("roles.errorCreateRole"));
      } finally {
        setSaving(false);
      }
    },
    [dialogOpen, orgUuid, createRoleFields],
  );

  const sortedRoles = useMemo(
    () =>
      [...roles].sort((a, b) =>
        a.is_admin === b.is_admin ? 0 : a.is_admin ? -1 : 1,
      ),
    [roles],
  );

  const roleColumns = useMemo<DataTableColumn<Role>[]>(
    () => [
      {
        id: "name",
        header: "Role",
        cell: (role) => (
          <div className="max-w-[440px] min-w-[200px]">
            <p
              className="truncate text-[13px] text-[var(--ink)]"
              title={role.name}
            >
              {role.name}
            </p>
            {role.description && (
              <p
                className="mt-px line-clamp-2 text-[10px] leading-snug text-[var(--ink-3)]"
                title={role.description}
              >
                {role.description}
              </p>
            )}
          </div>
        ),
      },
      {
        id: "type",
        header: "Type",
        cell: (role) => (
          <span
            className={cn(
              "inline-block w-fit rounded-[3px] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em]",
              role.is_admin
                ? "bg-[var(--green-bg)] text-[var(--green)]"
                : "bg-[var(--bg-subtle)] text-[var(--ink-3)]",
            )}
          >
            {role.is_admin ? "Admin" : "Custom"}
          </span>
        ),
      },
      {
        id: "permissions",
        header: "Permissions",
        cell: (role) => (
          <span className="text-[11px] text-[var(--ink-3)]">
            {role.is_admin
              ? "All"
              : `${(role.permission_codenames ?? []).length} permissions`}
          </span>
        ),
      },
      {
        id: "edit",
        header: "",
        cell: (role) => (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            data-testid={`roles-edit-${role.uuid}`}
            title={t("roles.renameRole", "Rename role")}
            onClick={(e) => {
              e.stopPropagation();
              setEditRole(role);
            }}
            className="ml-auto cursor-pointer"
          >
            <Pencil className="h-3 w-3 text-[var(--ink-3)]" />
          </Button>
        ),
      },
      {
        id: "arrow",
        header: "",
        cell: () => (
          <ChevronRight className="ml-auto h-3.5 w-3.5 text-[var(--ink-3)]" />
        ),
      },
    ],
    [t],
  );

  const handleRowClick = useCallback(
    (role: Role) => setSelectedRole(role),
    [],
  );

  if (!orgUuid) {
    return (
      <p className="clara-small py-8 text-center">{t("roles.noOrgSelected")}</p>
    );
  }

  return (
    <div>
      <StatCards
        cards={[
          { key: "total", label: "Total Roles", value: roles.length },
          {
            key: "admin",
            label: "Admin",
            value: roles.filter((r) => r.is_admin).length,
          },
          {
            key: "custom",
            label: "Custom",
            value: roles.filter((r) => !r.is_admin).length,
          },
          { key: "groups", label: "Templates", value: permissionGroups.length },
        ]}
      />
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <h3 className="clara-section-label">{t("roles.title")}</h3>
          <PageInfo>
            Roles define what members can do. Click a role to manage its
            permissions.
          </PageInfo>
        </div>
        <Button
          data-testid="roles-add-btn"
          onClick={() => setDialogOpen(true)}
          className="px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px] transition-all duration-150 cursor-pointer flex items-center gap-1.5 bg-[var(--green)] text-[var(--bg)] hover:bg-[var(--green-hover)]"
        >
          <Plus className="h-3 w-3" />
          {t("roles.createRole")}
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
        </div>
      ) : (
        <DataTable<Role>
          tableId="roles"
          columns={roleColumns}
          data={sortedRoles}
          rowKey="uuid"
          onRowClick={handleRowClick}
          rowTestId={(role) => `roles-item-${role.uuid}`}
          emptyMessage={t("roles.emptyState")}
        />
      )}
      <PermissionEditorModal
        open={selectedRole !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedRole(null);
        }}
        roleName={selectedRoleData?.name ?? ""}
        roleDescription={selectedRoleData?.description ?? ""}
        isAdmin={selectedRoleData?.is_admin ?? false}
        allPermissions={allPermissions}
        grantedCodenames={selectedGrantedSet}
        onToggle={(codename) =>
          selectedRoleData &&
          toggleCodenameOnRole(selectedRoleData, codename)
        }
        saving={savingRoleUuid === selectedRoleData?.uuid}
      />
      <RoleEditDialog
        open={editRole !== null}
        onOpenChange={(open) => {
          if (!open) setEditRole(null);
        }}
        title={t("roles.editRole", "Edit role")}
        initialName={editRole?.name ?? ""}
        initialDescription={editRole?.description ?? ""}
        saving={savingEdit}
        onSave={handleEditSave}
        roleUuid={editRole?.uuid}
      />
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            selectTemplate(null);
            createRoleFormRef.current?.resetForm({
              values: {
                name: "",
                description: "",
                sso_role_identifier: "",
              },
            });
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[15px] text-[var(--ink)]">
              {t("roles.createModalTitle", "Create role")}
            </DialogTitle>
            <p className="text-[12px] leading-snug text-[var(--ink-3)]">
              {t(
                "roles.createModalSubtitle",
                "Name the role and, optionally, start from a template. Fine-tune permissions once it exists.",
              )}
            </p>
          </DialogHeader>

          <CreateRoleForm className="space-y-4" />
          <RoleTemplateSuggestions
            groups={permissionGroups}
            selectedId={selectedTemplateId}
            onSelect={selectTemplate}
            disabled={saving}
          />

          <DialogFooter className="flex justify-end gap-3 border-t-[0.5px] border-t-[var(--rule)] pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="cursor-pointer px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
            >
              Cancel
            </Button>
            <Button
              data-testid="roles-dialog-submit"
              type="submit"
              form="roles-create-form"
              disabled={saving}
              className="cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 disabled:opacity-50"
            >
              {saving && (
                <Loader2 className="h-3 w-3 animate-spin mr-1.5 inline" />
              )}
              Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components (exported for external-roles-tab)
// ---------------------------------------------------------------------------

interface TemplateChipRowProps {
  sections: ReturnType<typeof groupsByEntity>;
  onApply: (group: PermissionGroup) => void;
  disabled: boolean;
  /** If true, hide groups that are not suitable for external assignment. */
  externalOnly?: boolean;
}

export function TemplateChipRow({
  sections,
  onApply,
  disabled,
  externalOnly = false,
}: TemplateChipRowProps) {
  const visibleSections = externalOnly
    ? sections
        .map((s) => ({
          ...s,
          groups: s.groups.filter(
            ({ group }) => group.is_suitable_for_external_assignment,
          ),
        }))
        .filter((s) => s.groups.length > 0)
    : sections;

  if (visibleSections.length === 0) {
    return <p className="clara-small py-2">No templates available.</p>;
  }

  return (
    <div className="mb-4 space-y-3 border-b-[0.5px] border-b-[var(--rule)] pb-3">
      {visibleSections.map(({ entity, groups }) => (
        <div key={entity}>
          <p className="mb-1.5 text-[9px] font-[400] uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {entity}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {groups.map(({ group, variant }) => (
              <Button
                key={group.id}
                type="button"
                variant="ghost"
                onClick={() => onApply(group)}
                disabled={disabled}
                title={group.description}
                className={cn(
                  "inline-flex items-center gap-1 rounded-[4px] px-2.5 py-[4px] text-[10px] transition-all duration-150 cursor-pointer border-[0.5px]",
                  variant === "manage" || variant === "manage-own"
                    ? "border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)] hover:bg-[var(--green)] hover:text-[var(--bg)]"
                    : "border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-3)] hover:border-[var(--ink-3)] hover:text-[var(--ink)]",
                  disabled && "opacity-50 cursor-not-allowed",
                )}
              >
                <Plus className="h-2.5 w-2.5" />
                {group.name.replace(`${entity}`, "").trim() || group.name}
              </Button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
